import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  FileText,
  History,
  Loader2,
  MessageSquare,
  Paperclip,
  PlusCircle,
  Send,
  Share2,
  Tag,
  TicketCheck,
  User,
  Users,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const UNASSIGNED_VALUE = '__unassigned__';

const statusConfig = {
  aberto: { label: 'Aberto', classes: 'bg-blue-100 text-blue-700 border-blue-200' },
  em_andamento: { label: 'Em andamento', classes: 'bg-purple-100 text-purple-700 border-purple-200' },
  aguardando_aprovacao: { label: 'Aguardando aprovação', classes: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  em_aprovacao: { label: 'Em aprovação', classes: 'bg-orange-100 text-orange-700 border-orange-200' },
  aprovado: { label: 'Aprovado', classes: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejeitado: { label: 'Rejeitado', classes: 'bg-red-100 text-red-700 border-red-200' },
  resolvido: { label: 'Resolvido', classes: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  fechado: { label: 'Fechado', classes: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const priorityConfig = {
  baixa: { label: 'Baixa', classes: 'bg-slate-100 text-slate-700 border-slate-200' },
  media: { label: 'Média', classes: 'bg-blue-100 text-blue-700 border-blue-200' },
  alta: { label: 'Alta', classes: 'bg-orange-100 text-orange-700 border-orange-200' },
  critica: { label: 'Crítica', classes: 'bg-red-100 text-red-700 border-red-200' },
};

const menuItems = [
  {
    id: 'tickets',
    name: 'Chamados',
    icon: TicketCheck,
    description: 'Gerenciar chamados',
    submenus: [
      { id: 'dashboard', name: 'Dashboard', icon: FileText },
      { id: 'kanban', name: 'Kanban', icon: Share2 },
      { id: 'novo', name: 'Novo Chamado', icon: PlusCircle },
      { id: 'meus', name: 'Meus Chamados', icon: User },
      { id: 'todos', name: 'Todos Chamados', icon: TicketCheck },
    ],
  },
];

const roleLabel = {
  requester: 'Solicitante',
  manager: 'Superior direto',
  agent: 'Atendente',
  admin: 'Administrador',
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [slaTemplates, setSlaTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ tickets: true });
  const [activeTab, setActiveTab] = useState('details');
  const [editData, setEditData] = useState({});
  const [solucao, setSolucao] = useState('');

  const fetchAuxData = useCallback(async () => {
    const [deptRes, userRes, slaRes] = await Promise.all([
      axios.get(`${API}/departments`),
      axios.get(`${API}/users`),
      axios.get(`${API}/sla-templates`),
    ]);
    setDepartments(deptRes.data || []);
    setUsers(userRes.data || []);
    setSlaTemplates(slaRes.data || []);
  }, []);

  const fetchTicket = useCallback(async () => {
    const res = await axios.get(`${API}/tickets/${id}`);
    setTicket(res.data);
    setEditData({
      status: res.data.status || 'aberto',
      priority: res.data.priority || 'media',
      assigned_to_id: res.data.assigned_to_id || UNASSIGNED_VALUE,
      sla_template_id: res.data.sla_template_id || 'none',
    });
    setSolucao(res.data.solucao || '');
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTicket(), fetchAuxData()])
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Erro ao carregar chamado');
        navigate('/dashboard');
      })
      .finally(() => setLoading(false));
  }, [fetchAuxData, fetchTicket, navigate]);

  const viewerRole = ticket?.viewer_role || 'requester';
  const isAgentView = ['agent', 'admin'].includes(viewerRole);

  const handleTicketUpdate = async (payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/tickets/${id}`, payload);
      await fetchTicket();
      toast.success('Chamado atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar chamado');
    } finally {
      setSaving(false);
    }
  };

  const getUserName = useCallback((userId) => {
    if (!userId) return 'Não atribuído';
    const found = users.find((item) => item.id === userId);
    return found?.name || 'Usuário';
  }, [users]);

  const getDepartmentName = useCallback((departmentId) => {
    if (!departmentId) return 'Sem departamento';
    const found = departments.find((item) => item.id === departmentId);
    return found?.name || 'Departamento';
  }, [departments]);

  const getSlaName = useCallback((slaId) => {
    if (!slaId || slaId === 'none') return 'Sem SLA';
    const found = slaTemplates.find((item) => item.id === slaId);
    return found?.name || 'SLA';
  }, [slaTemplates]);

  const statusBadge = statusConfig[ticket?.status] || statusConfig.aberto;
  const priorityBadge = priorityConfig[ticket?.priority] || priorityConfig.media;

  const shellTabChange = () => navigate('/dashboard');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar
        menuItems={menuItems}
        activeTab="tickets"
        activeSubTab="todos"
        expandedMenus={expandedMenus}
        sidebarCollapsed={sidebarCollapsed}
        user={user}
        setActiveTab={shellTabChange}
        setActiveSubTab={shellTabChange}
        toggleMenu={(menuId) => setExpandedMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }))}
        setSidebarCollapsed={setSidebarCollapsed}
        logout={logout}
        setShowNewTicketDialog={() => navigate('/dashboard')}
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <Header menuItems={menuItems} activeTab="tickets" user={user} />

        <main className="flex-1 overflow-auto">
          <div className="p-6 lg:p-8 space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Voltar
                      </Button>
                      <Badge variant="outline">{roleLabel[viewerRole] || viewerRole}</Badge>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500">{ticket.ticket_number}</p>
                      <h1 className="text-2xl font-semibold text-slate-900">{ticket.title}</h1>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={statusBadge.classes}>{statusBadge.label}</Badge>
                      <Badge variant="outline" className={priorityBadge.classes}>{priorityBadge.label}</Badge>
                      <Badge variant="outline">{ticket.category_name || 'Sem categoria'}</Badge>
                      <Badge variant="outline">{ticket.department_name || getDepartmentName(ticket.department_id)}</Badge>
                    </div>
                  </div>

                  <Link to="/dashboard">
                    <Button variant="outline">Dashboard</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              <div className="xl:col-span-3 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Descrição</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                      <TabsList className={`grid w-full ${isAgentView ? 'grid-cols-4' : 'grid-cols-3'}`}>
                        <TabsTrigger value="details">Detalhes</TabsTrigger>
                        <TabsTrigger value="comments">Comentários</TabsTrigger>
                        <TabsTrigger value="history">Histórico</TabsTrigger>
                        {isAgentView && <TabsTrigger value="attachments">Anexos</TabsTrigger>}
                      </TabsList>

                      <TabsContent value="details" className="mt-6">
                        {isAgentView ? (
                          <AgentWorkspace
                            ticket={ticket}
                            departments={departments}
                            users={users}
                            slaTemplates={slaTemplates}
                            editData={editData}
                            setEditData={setEditData}
                            solucao={solucao}
                            setSolucao={setSolucao}
                            saving={saving}
                            onUpdate={handleTicketUpdate}
                            onRefresh={fetchTicket}
                          />
                        ) : (
                          <RequesterWorkspace ticket={ticket} />
                        )}
                      </TabsContent>

                      <TabsContent value="comments" className="mt-6">
                        <CommentsTab ticket={ticket} onRefresh={fetchTicket} />
                      </TabsContent>

                      <TabsContent value="history" className="mt-6">
                        <HistoryTab ticketId={id} />
                      </TabsContent>

                      {isAgentView && (
                        <TabsContent value="attachments" className="mt-6">
                          <AttachmentsTab ticket={ticket} />
                        </TabsContent>
                      )}
                    </Tabs>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4">
                <MetadataCard
                  ticket={ticket}
                  getUserName={getUserName}
                  getDepartmentName={getDepartmentName}
                  getSlaName={getSlaName}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const AgentWorkspace = ({
  ticket,
  departments,
  users,
  slaTemplates,
  editData,
  setEditData,
  solucao,
  setSolucao,
  saving,
  onUpdate,
  onRefresh,
}) => {
  const [dispatchData, setDispatchData] = useState({ department_id: '', description: '', title: '' });
  const [relationData, setRelationData] = useState({ target_ticket_id: '', relation_type: 'relacionado', note: '' });
  const [submittingDispatch, setSubmittingDispatch] = useState(false);
  const [submittingRelation, setSubmittingRelation] = useState(false);

  const activeDepartments = useMemo(
    () => departments.filter((department) => department.id !== ticket.department_id),
    [departments, ticket.department_id]
  );

  const submitDispatch = async () => {
    if (!dispatchData.department_id || !dispatchData.description.trim()) {
      toast.error('Informe departamento e motivo do acionamento');
      return;
    }

    setSubmittingDispatch(true);
    try {
      await axios.post(`${API}/tickets/${ticket.id}/dispatches`, {
        department_id: dispatchData.department_id,
        description: dispatchData.description.trim(),
        title: dispatchData.title.trim() || undefined,
      });
      toast.success('Subchamado criado');
      setDispatchData({ department_id: '', description: '', title: '' });
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao acionar departamento');
    } finally {
      setSubmittingDispatch(false);
    }
  };

  const submitRelation = async () => {
    if (!relationData.target_ticket_id.trim()) {
      toast.error('Informe o ID do chamado relacionado');
      return;
    }

    setSubmittingRelation(true);
    try {
      await axios.post(`${API}/tickets/${ticket.id}/relations`, {
        target_ticket_id: relationData.target_ticket_id.trim(),
        relation_type: relationData.relation_type,
        note: relationData.note.trim() || null,
      });
      toast.success('Relação criada');
      setRelationData({ target_ticket_id: '', relation_type: 'relacionado', note: '' });
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao relacionar chamado');
    } finally {
      setSubmittingRelation(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Operação do atendimento</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldSelect
            label="Status"
            value={editData.status}
            options={[
              ['aberto', 'Aberto'],
              ['em_andamento', 'Em andamento'],
              ['aguardando_aprovacao', 'Aguardando aprovação'],
              ['em_aprovacao', 'Em aprovação'],
              ['aprovado', 'Aprovado'],
              ['rejeitado', 'Rejeitado'],
              ['resolvido', 'Resolvido'],
              ['fechado', 'Fechado'],
            ]}
            onChange={(value) => {
              setEditData((prev) => ({ ...prev, status: value }));
              onUpdate({ status: value, solucao });
            }}
            disabled={saving}
          />

          <FieldSelect
            label="Prioridade"
            value={editData.priority}
            options={[
              ['baixa', 'Baixa'],
              ['media', 'Média'],
              ['alta', 'Alta'],
              ['critica', 'Crítica'],
            ]}
            onChange={(value) => {
              setEditData((prev) => ({ ...prev, priority: value }));
              onUpdate({ priority: value });
            }}
            disabled={saving}
          />

          <FieldSelect
            label="Responsável"
            value={editData.assigned_to_id || UNASSIGNED_VALUE}
            options={[
              [UNASSIGNED_VALUE, 'Não atribuído'],
              ...users.map((item) => [item.id, item.name]),
            ]}
            onChange={(value) => {
              setEditData((prev) => ({ ...prev, assigned_to_id: value }));
              onUpdate({ assigned_to_id: value === UNASSIGNED_VALUE ? null : value });
            }}
            disabled={saving}
          />

          <FieldSelect
            label="SLA"
            value={editData.sla_template_id || 'none'}
            options={[
              ['none', 'Sem SLA'],
              ...slaTemplates.map((item) => [item.id, item.name]),
            ]}
            onChange={(value) => {
              setEditData((prev) => ({ ...prev, sla_template_id: value }));
              onUpdate({ sla_template_id: value === 'none' ? null : value });
            }}
            disabled={saving}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Solução do chamado</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={solucao}
            onChange={(event) => setSolucao(event.target.value)}
            placeholder="Registre a solução consolidada do chamado principal"
            className="min-h-[120px]"
          />
          <div className="flex justify-end">
            <Button onClick={() => onUpdate({ solucao })} disabled={saving || !solucao.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar solução
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Acionar outro departamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <FieldSelect
              label="Departamento"
              value={dispatchData.department_id}
              options={activeDepartments.map((item) => [item.id, item.name])}
              onChange={(value) => setDispatchData((prev) => ({ ...prev, department_id: value }))}
            />
            <div className="space-y-2">
              <Label>Título opcional do subchamado</Label>
              <Input
                value={dispatchData.title}
                onChange={(event) => setDispatchData((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="[Apoio] ..."
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Textarea
                value={dispatchData.description}
                onChange={(event) => setDispatchData((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Descreva o que o outro departamento precisa atender"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={submitDispatch} disabled={submittingDispatch}>
                {submittingDispatch && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Criar subchamado
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Relacionar chamado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>ID do chamado alvo</Label>
              <Input
                value={relationData.target_ticket_id}
                onChange={(event) => setRelationData((prev) => ({ ...prev, target_ticket_id: event.target.value }))}
                placeholder="UUID do chamado relacionado"
              />
            </div>
            <FieldSelect
              label="Tipo de relação"
              value={relationData.relation_type}
              options={[
                ['relacionado', 'Relacionado'],
                ['duplicado', 'Duplicado'],
                ['depende_de', 'Depende de'],
                ['bloqueia', 'Bloqueia'],
              ]}
              onChange={(value) => setRelationData((prev) => ({ ...prev, relation_type: value }))}
            />
            <div className="space-y-2">
              <Label>Observação</Label>
              <Textarea
                value={relationData.note}
                onChange={(event) => setRelationData((prev) => ({ ...prev, note: event.target.value }))}
                placeholder="Contexto da relação"
              />
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={submitRelation} disabled={submittingRelation}>
                {submittingRelation && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Relacionar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <TicketChildrenSection ticket={ticket} />
      <RelationsSection ticket={ticket} />
    </div>
  );
};

const RequesterWorkspace = ({ ticket }) => (
  <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle>Acompanhamento do chamado</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <SummaryItem label="Solicitante" value={ticket.requester_name || '—'} icon={User} />
        <SummaryItem label="Responsável atual" value={ticket.assigned_to_name || 'Não atribuído'} icon={Users} />
        <SummaryItem label="Departamento responsável" value={ticket.department_name || '—'} icon={Building2} />
        <SummaryItem label="SLA" value={ticket.sla_name || 'Sem SLA'} icon={Clock} />
      </CardContent>
    </Card>

    <TicketChildrenSection ticket={ticket} readOnly />

    {ticket.solucao && (
      <Card>
        <CardHeader>
          <CardTitle>Solução informada</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.solucao}</p>
        </CardContent>
      </Card>
    )}
  </div>
);

const CommentsTab = ({ ticket, onRefresh }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ content: '', visibility: 'publico' });

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tickets/${ticket.id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar comentários');
    } finally {
      setLoading(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const submit = async () => {
    if (!formData.content.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/tickets/${ticket.id}/comments`, {
        content: formData.content.trim(),
        visibility: formData.visibility,
      });
      setFormData({ content: '', visibility: 'publico' });
      await loadComments();
      await onRefresh();
      toast.success('Comentário adicionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao comentar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Novo apontamento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {ticket.can_comment_internal && (
            <FieldSelect
              label="Visibilidade"
              value={formData.visibility}
              options={[
                ['publico', 'Público'],
                ['interno', 'Interno'],
              ]}
              onChange={(value) => setFormData((prev) => ({ ...prev, visibility: value }))}
            />
          )}
          <Textarea
            value={formData.content}
            onChange={(event) => setFormData((prev) => ({ ...prev, content: event.target.value }))}
            placeholder={ticket.can_comment_internal ? 'Registre andamento público ou nota interna' : 'Digite seu comentário'}
            className="min-h-[100px]"
          />
          <div className="flex justify-end">
            <Button onClick={submit} disabled={submitting || !formData.content.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Enviar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Histórico de comentários</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum comentário registrado.</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-900">{comment.author_name || 'Usuário'}</span>
                    <Badge variant="outline">
                      {comment.visibility === 'interno' ? 'Interno' : 'Público'}
                    </Badge>
                  </div>
                  <span className="text-xs text-slate-500">
                    {formatDateTime(comment.created_at)}
                  </span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.content}</p>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const HistoryTab = ({ ticketId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/tickets/${ticketId}/history`);
        setHistory(response.data || []);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao carregar histórico');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [ticketId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhuma alteração registrada.</p>
      ) : (
        history.map((item) => (
          <div key={item.id} className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{item.field_changed}</p>
                <p className="text-sm text-slate-600">
                  {item.old_value && item.new_value
                    ? `${item.old_value} → ${item.new_value}`
                    : item.new_value || item.old_value || 'Alteração registrada'}
                </p>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p>{item.user_name || 'Sistema'}</p>
                <p>{formatDateTime(item.created_at)}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const AttachmentsTab = ({ ticket }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tickets/${ticket.id}/attachments`);
      setAttachments(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar anexos');
    } finally {
      setLoading(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/tickets/${ticket.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Arquivo anexado');
      await loadAttachments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar arquivo');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input type="file" onChange={onUpload} disabled={uploading} className="max-w-md" />
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
      </div>

      <Separator />

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum anexo registrado.</p>
      ) : (
        attachments.map((item) => (
          <div key={item.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <p className="text-sm font-medium text-slate-900">{item.filename}</p>
              <p className="text-xs text-slate-500">
                {item.user_name || 'Usuário'} • {formatDateTime(item.created_at)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`${API}/tickets/${ticket.id}/attachments/${item.id}/download`, '_blank')}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ))
      )}
    </div>
  );
};

const TicketChildrenSection = ({ ticket, readOnly = false }) => (
  <Card>
    <CardHeader>
      <CardTitle>{readOnly ? 'Subchamados relacionados' : 'Hierarquia e subchamados'}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {ticket.parent_ticket_id && (
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Chamado pai</p>
          <p className="text-sm text-slate-700">{ticket.parent_ticket_id}</p>
        </div>
      )}

      {ticket.department_dispatches?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-900">Acionamentos de departamento</p>
          {ticket.department_dispatches.map((item) => (
            <div key={item.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {item.department_name || 'Departamento'} {item.child_ticket_number ? `• ${item.child_ticket_number}` : ''}
                  </p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{item.reason}</p>
                </div>
                <Badge variant="outline">{item.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {ticket.child_tickets?.length > 0 ? (
        <div className="space-y-2">
          {ticket.child_tickets.map((child) => (
            <Link
              key={child.id}
              to={`/tickets/${child.id}`}
              className="block rounded-lg border border-slate-200 p-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{child.ticket_number}</p>
                  <p className="text-sm text-slate-600">{child.title}</p>
                </div>
                <Badge variant="outline">{statusConfig[child.status]?.label || child.status}</Badge>
              </div>
            </Link>
          ))}
        </div>
      ) : !ticket.department_dispatches?.length ? (
        <p className="text-sm text-slate-500">Nenhum subchamado vinculado.</p>
      ) : null}
    </CardContent>
  </Card>
);

const RelationsSection = ({ ticket }) => (
  <Card>
    <CardHeader>
      <CardTitle>Relações com outros chamados</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {ticket.related_tickets?.length > 0 ? (
        ticket.related_tickets.map((relation) => (
          <div key={relation.id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {relation.relation_type} • {relation.target_ticket_number || relation.target_ticket_id}
                </p>
                <p className="text-sm text-slate-600">{relation.target_ticket_title || 'Chamado relacionado'}</p>
                {relation.note && <p className="text-xs text-slate-500 mt-1">{relation.note}</p>}
              </div>
              <span className="text-xs text-slate-500">{formatDateTime(relation.created_at)}</span>
            </div>
          </div>
        ))
      ) : (
        <p className="text-sm text-slate-500">Nenhuma relação cadastrada.</p>
      )}
    </CardContent>
  </Card>
);

const MetadataCard = ({ ticket, getUserName, getDepartmentName, getSlaName }) => (
  <>
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Metadados</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SummaryItem label="Solicitante" value={getUserName(ticket.created_by)} icon={User} />
        <SummaryItem label="Responsável" value={getUserName(ticket.assigned_to_id)} icon={Users} />
        <SummaryItem label="Departamento" value={getDepartmentName(ticket.department_id)} icon={Building2} />
        <SummaryItem label="Categoria" value={ticket.category_name || 'Sem categoria'} icon={Tag} />
        <SummaryItem label="SLA" value={getSlaName(ticket.sla_template_id)} icon={Clock} />
        <SummaryItem label="Criado em" value={formatDateTime(ticket.created_at)} icon={Calendar} />
        <SummaryItem label="Atualizado em" value={formatDateTime(ticket.updated_at)} icon={History} />
      </CardContent>
    </Card>

    {ticket.solucao && (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Resumo da solução</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.solucao}</p>
        </CardContent>
      </Card>
    )}
  </>
);

const SummaryItem = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-4 w-4 text-slate-400 mt-0.5" />
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-slate-900">{value}</p>
    </div>
  </div>
);

const FieldSelect = ({ label, value, options, onChange, disabled = false }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue placeholder={`Selecione ${label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {options.map(([optionValue, optionLabel]) => (
          <SelectItem key={optionValue} value={optionValue}>
            {optionLabel}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return '—';
  }
};

export default TicketDetailPage;
